'use client';

import { useState } from 'react';
import Link from 'next/link';
import { ChevronLeft, Link as LinkIcon, Check } from 'lucide-react';
import { useLanguage } from '@/components/LanguageProvider'; 

export default function PrivacyPolicy() {
  const { t } = useLanguage();
  const p = t.privacy; 

  return (
    <div className="w-full">
      
      {/* Шапка самого документа */}
      <div className="mb-12 md:mb-16">
          <Link href="/legal" className="inline-flex items-center gap-2 text-[#aaaaaa] hover:text-white transition-colors mb-8 group font-medium text-sm md:text-base outline-none">
              <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
              {p.backBtn}
          </Link>
          <h1 className="font-mc text-4xl md:text-5xl text-white mb-4 uppercase tracking-tight">
              {p.title}
          </h1>
          <p className="text-[#aaaaaa] font-medium">
              {p.lastUpdated} <span className="text-white">{p.date}</span>
          </p>
      </div>

      <div className="space-y-16 legal-content">
          
          <Section id="intro" title={p.intro.title} copyText={p.copyLink}>
              <p>{p.intro.p1}</p>
              <p>{p.intro.p2Start}<span className="text-white font-medium">{p.intro.p2Bold}</span>{p.intro.p2End}</p>
              <p>{p.intro.p3}</p>
              
              <div className="bg-[#161616] p-5 rounded-[16px] border border-white/5 mt-6 text-sm">
                  <strong className="text-white">{p.intro.noteLabel}</strong>{p.intro.noteText}
              </div>
          </Section>

          <Section id="collect" title={p.s1.title} copyText={p.copyLink}>
              <p>{p.s1.desc}</p>
              
              <h3 className="text-white font-bold text-lg mt-8 mb-4">{p.s1.subtitleA}</h3>
              <ul className="list-disc pl-5 mt-4 space-y-4 text-[#aaaaaa]">
                  {p.s1.listA.map((item, idx) => (
                    <li key={idx}>
                      <strong className="text-white font-medium">{item.label}</strong>{item.text}
                    </li>
                  ))}
              </ul>

              <h3 className="text-white font-bold text-lg mt-10 mb-4">{p.s1.subtitleB}</h3>
              <ul className="list-disc pl-5 mt-4 space-y-4 text-[#aaaaaa]">
                  {p.s1.listB.map((item, idx) => (
                    <li key={idx}>
                      {/* Специальное условие для последнего пункта в S1 B */}
                      {item.isEnd ? (
                        <>
                          <strong className="text-white font-medium">{item.label}</strong>{item.text}
                        </>
                      ) : (
                        <>
                          <strong className="text-white font-medium">{item.label}</strong>{item.text}
                        </>
                      )}
                    </li>
                  ))}
              </ul>
          </Section>

          <Section id="purpose" title={p.s2.title} copyText={p.copyLink}>
              <p>{p.s2.desc}</p>
              <ul className="list-disc pl-5 mt-4 space-y-2 text-[#aaaaaa]">
                  {p.s2.list.map((text, idx) => (
                    <li key={idx}>{text}</li>
                  ))}
              </ul>
          </Section>

          <Section id="legal" title={p.s3.title} copyText={p.copyLink}>
              <p>{p.s3.desc}</p>
              <ol className="list-decimal pl-5 mt-4 space-y-4 text-[#aaaaaa]">
                  {p.s3.list.map((item, idx) => (
                    <li key={idx}>
                      <strong className="text-white font-medium">{item.label}</strong>{item.text}
                    </li>
                  ))}
              </ol>
          </Section>

          <Section id="ads" title={p.s4.title} copyText={p.copyLink}>
              <p>{p.s4.p1}</p>
              <p>{p.s4.p2}</p>
          </Section>

          <Section id="cookies" title={p.s5.title} copyText={p.copyLink}>
              <p>{p.s5.p1}</p>
              <p>{p.s5.p2}</p>
              <p>{p.s5.p3}</p>
          </Section>

          <Section id="sharing" title={p.s6.title} copyText={p.copyLink}>
              <p>{p.s6.desc}</p>
              <ul className="list-disc pl-5 mt-4 space-y-4 text-[#aaaaaa]">
                  {p.s6.list.map((item, idx) => (
                    <li key={idx}>
                      <strong className="text-white font-medium block mb-1">{item.label}</strong>
                      {item.text}
                      {item.subtext && <p className="mt-2 text-sm">{item.subtext}</p>}
                    </li>
                  ))}
              </ul>
              
              <div className="bg-[#161616] p-5 rounded-[16px] border border-white/5 mt-6 text-sm">
                  <strong className="text-white">{p.s6.noteLabel}</strong>{p.s6.noteText}
              </div>
          </Section>

          <Section id="retention" title={p.s7.title} copyText={p.copyLink}>
              <p>{p.s7.p1}</p>
              <p>{p.s7.p2Start}<strong className="text-white font-medium">{p.s7.p2Bold}</strong>{p.s7.p2End}</p>
              <p>{p.s7.p3}</p>
              <p>{p.s7.p4}</p>
          </Section>

          <Section id="rights" title={p.s8.title} copyText={p.copyLink}>
              <p>{p.s8.desc}</p>
              <ul className="list-disc pl-5 mt-4 space-y-2 text-[#aaaaaa]">
                  {p.s8.list.map((text, idx) => (
                    <li key={idx}>{text}</li>
                  ))}
              </ul>
              <p className="mt-6">
                  {p.s8.contactPre}<a href="mailto:privacy@royolands.com">privacy@royolands.com</a>.
              </p>
          </Section>

          <Section id="security" title={p.s9.title} copyText={p.copyLink}>
              <p>{p.s9.p1}</p>
              <p><strong className="text-white font-medium">{p.s9.p2Label}</strong>{p.s9.p2Text}</p>
          </Section>

          <Section id="children" title={p.s10.title} copyText={p.copyLink}>
              <p>{p.s10.p1}</p>
              <p>{p.s10.p2}</p>
          </Section>

          <Section id="changes" title={p.s11.title} copyText={p.copyLink}>
              <p>{p.s11.p1}</p>
          </Section>

          <Section id="contact" title={p.s12.title} copyText={p.copyLink}>
              <div className="bg-[#161616] border border-white/5 rounded-[16px] p-6 md:p-8 mt-4">
                  <p className="mb-6 text-white font-medium">{p.s12.desc}</p>
                  <div className="space-y-2 text-[#aaaaaa]">
                      <p className="mb-0"><strong>{p.s12.emailLabel}</strong> <a href="mailto:privacy@royolands.com">privacy@royolands.com</a></p>
                      <p className="mb-0"><strong className="text-white font-medium">{p.s12.legalLabel}</strong> {p.s12.legalName}</p>
                      <p className="mb-0">{p.s12.inn}</p>
                      <p className="mb-0">{p.s12.ogrnip}</p>
                      <p className="mb-0"><strong className="text-white font-medium">{p.s12.addressLabel}</strong> {p.s12.address}</p>
                  </div>
              </div>
          </Section>

          <Section id="supplemental" title={p.s13.title} copyText={p.copyLink}>
              <p><strong className="text-white font-medium">{p.s13.ruLabel}</strong>{p.s13.ruText}</p>
              <p><strong className="text-white font-medium">{p.s13.euLabel}</strong>{p.s13.euText}</p>
              <p><strong className="text-white font-medium">{p.s13.usLabel}</strong>{p.s13.usText}</p>
          </Section>

      </div>
    </div>
  );
}

// Мини-обертка секций с функцией копирования ссылки (добавил prop copyText)
function Section({ id, title, copyText, children }: { id: string, title: string, copyText: string, children: React.ReactNode }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const url = `${window.location.origin}${window.location.pathname}#${id}`;
        navigator.clipboard.writeText(url);
        
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        
        window.history.pushState(null, '', `#${id}`);
    };

    return (
        <section id={id} data-toc={title} className="scroll-mt-32 group/section">
            <h2 className="font-mc text-2xl text-white uppercase tracking-tight mb-5 flex items-center gap-3">
                {title}
                
                <button
                    onClick={handleCopy}
                    className="opacity-0 group-hover/section:opacity-100 transition-all duration-200 text-[#aaaaaa] hover:text-white p-1.5 rounded-md bg-[#161616] border border-white/5 hover:bg-white/10 outline-none cursor-pointer"
                    title={copyText}
                    aria-label={copyText}
                >
                    {copied ? <Check size={16} className="text-[#4cc91f]" /> : <LinkIcon size={16} />}
                </button>
            </h2>
            <div className="text-[#aaaaaa]">
                {children}
            </div>
        </section>
    );
}